import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertTriangle, Wrench, XCircle, Activity } from 'lucide-react';
import api from '@/lib/api';

const STATUS_META = {
  operational: { label: 'Betriebsbereit', color: '#2EF2A5', Icon: CheckCircle2 },
  degraded: { label: 'Eingeschränkt', color: '#FFCC66', Icon: AlertTriangle },
  maintenance: { label: 'Wartung', color: '#5AF3FF', Icon: Wrench },
  outage: { label: 'Störung', color: '#FF5C7A', Icon: XCircle },
};

export default function StatusPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = () => api.get('/status').then((r) => { if (alive) setData(r.data); }).catch(() => { if (alive) setData(null); }).finally(() => { if (alive) setLoading(false); });
    load();
    const i = setInterval(load, 20000);
    return () => { alive = false; clearInterval(i); };
  }, []);

  const overall = data?.overall || 'operational';
  const meta = STATUS_META[overall] || STATUS_META.operational;

  return (
    <div className="min-h-screen pt-28 pb-24 gradient-hero" data-testid="status-page">
      <div className="absolute inset-0 itw-grid opacity-30 pointer-events-none" />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-[color:var(--itw-cyan)]">
          <Activity className="h-3.5 w-3.5" /> Systemstatus
        </div>

        {/* overall banner */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="mt-4 glass rounded-2xl border border-[color:var(--itw-border-strong)] p-6 sm:p-8 flex items-center gap-4"
          data-testid="status-overall">
          <span className="h-12 w-12 rounded-xl grid place-items-center" style={{ background: `${meta.color}18` }}>
            <meta.Icon className="h-6 w-6" style={{ color: meta.color }} />
          </span>
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-[color:var(--itw-text)]">
              {overall === 'operational' ? 'Alle Systeme betriebsbereit' : `Status: ${meta.label}`}
            </h1>
            <p className="mt-1 text-sm text-[color:var(--itw-text-2)]">Aktueller Betriebsstatus unserer Dienste · automatische Aktualisierung</p>
          </div>
        </motion.div>

        {loading && <div className="mt-8 text-sm text-[color:var(--itw-text-3)] font-mono">lade Status…</div>}

        {/* services */}
        {data && (
          <div className="mt-6 space-y-2.5" data-testid="status-services">
            {data.services.map((s, i) => {
              const m = STATUS_META[s.status] || STATUS_META.operational;
              return (
                <motion.div key={s.name} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                  className="flex items-center justify-between rounded-xl border border-[color:var(--itw-border)] bg-[color:var(--itw-surface)]/70 px-4 py-3.5">
                  <div>
                    <div className="text-sm font-medium text-[color:var(--itw-text)]">{s.name}</div>
                    {s.category && <div className="text-[11px] text-[color:var(--itw-text-3)] font-mono">{s.category}</div>}
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: m.color }}>
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: m.color }} />{m.label}
                  </span>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* incidents */}
        {data?.incidents?.length > 0 && (
          <div className="mt-8" data-testid="status-incidents">
            <h2 className="text-sm font-semibold text-[color:var(--itw-text)] mb-2">Aktuelle Störungen</h2>
            <div className="space-y-2">
              {data.incidents.map((inc) => (
                <div key={inc.id} className="rounded-xl border border-[#FF5C7A]/30 bg-[#FF5C7A]/8 p-4">
                  <div className="flex items-center justify-between"><span className="text-sm font-medium text-[color:var(--itw-text)]">{inc.title}</span><span className="text-[11px] font-mono text-[#FF5C7A] uppercase">{inc.status}</span></div>
                  {inc.message && <p className="mt-1 text-sm text-[color:var(--itw-text-2)]">{inc.message}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* maintenance */}
        {data?.maintenance?.length > 0 && (
          <div className="mt-8" data-testid="status-maintenance">
            <h2 className="text-sm font-semibold text-[color:var(--itw-text)] mb-2">Geplante Wartungen</h2>
            <div className="space-y-2">
              {data.maintenance.map((mt) => (
                <div key={mt.id} className="rounded-xl border border-[#5AF3FF]/30 bg-[#5AF3FF]/8 p-4 text-sm text-[color:var(--itw-text-2)]">{mt.title}</div>
              ))}
            </div>
          </div>
        )}

        <p className="mt-10 text-[11px] text-[color:var(--itw-text-3)] font-mono text-center">
          Diese Seite zeigt ausschließlich allgemeine Service-Status. Interne Infrastrukturdaten sind nur im geschützten Admin-Bereich sichtbar.
        </p>
      </div>
    </div>
  );
}
